/**
 * <skincu-count> counts a number up from zero the first time it scrolls into view.
 *
 * The final value is rendered by Liquid, so it is what shows without JavaScript, for
 * search engines and for visitors who prefer reduced motion. Only values made of one
 * number with an optional prefix and suffix are animated ("98%", "1,805", "$28"), so a
 * range like "2-4" stays as written.
 */
if (!customElements.get('skincu-count')) {
  class SkincuCount extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const finalText = this.textContent.trim();
      const match = finalText.match(/^(\D*)(\d[\d.,]*)(\D*)$/);

      if (!match || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        return;
      }

      const [, prefix, rawNumber, suffix] = match;
      const decimalMatch = rawNumber.match(/\.(\d+)$/);
      const decimals = decimalMatch ? decimalMatch[1].length : 0;
      const usesThousands = rawNumber.includes(',');
      const target = parseFloat(rawNumber.replace(/,/g, ''));

      if (!isFinite(target)) return;

      const format = (value) => {
        let output = value.toFixed(decimals);

        if (usesThousands) {
          const parts = output.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          output = parts.join('.');
        }

        return prefix + output + suffix;
      };

      // Hold the width of the final value so the digits never push the layout around.
      this.style.display = 'inline-block';
      this.style.minWidth = `${Math.ceil(this.getBoundingClientRect().width)}px`;
      this.style.fontVariantNumeric = 'tabular-nums';
      this.setAttribute('aria-label', finalText);
      this.textContent = format(0);

      const duration = (parseFloat(this.getAttribute('duration')) || 2) * 1000;
      const delay = parseInt(this.getAttribute('delay'), 10) || 0;
      const card = this.closest('[data-skincu-count-card]');

      if (card) card.classList.add('is-count-ready');

      const run = () => {
        if (card) card.classList.add('is-counting');

        const start = performance.now();

        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          // Ease out quart, fast at first and settling gently on the final value.
          const eased = 1 - Math.pow(1 - progress, 4);

          this.textContent = format(target * eased);

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            this.textContent = finalText;
          }
        };

        requestAnimationFrame(tick);
      };

      this._observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;

          this._observer.disconnect();
          setTimeout(run, delay);
        },
        { threshold: 0.6 }
      );

      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }
  }

  customElements.define('skincu-count', SkincuCount);
}
